import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { API_URL } from "../config/api";
import { buildGemCatalog } from "../constants/gems";
import { getAppPortalRoot } from "../utils/appPortal";
import MenuIconTooltip from "./MenuIconTooltip";
import PackAssignDropdown from "./PackAssignDropdown";
import VocabInfoTip from "./VocabInfoTip";

const GEM_BY_ID = new Map(buildGemCatalog().map((gem) => [gem.id, gem]));
const FILLER_GEM_ORDER = [
  "pyrite",
  "nacre",
  "ambre",
  "corail",
  "quartz rose",
  "quartz fumé",
  "amazonite",
  "onyx",
  "pierre de lune",
  "lapiz lazuli",
  "tourmaline",
  "jade",
];

const QUANTITY_MAX = 1000;
const FEMININE_GEM_IDS = new Set(["tanzanite", "émeraude"]);
const GEM_RATE_ARTICLE = {
  spinelle: "du",
  péridot: "du",
  tanzanite: "de la",
  émeraude: "de l'",
  saphir: "du",
  rubis: "du",
  diamant: "du",
};
const COMPARISON_OPTIONS = [
  { value: "1", label: "J-1" },
  { value: "7", label: "J-7" },
  { value: "10", label: "J-10" },
  { value: "30", label: "J-30" },
];

function basketTooltip(gem, basket) {
  const article = GEM_RATE_ARTICLE[gem.id] || "du";
  const name = String(gem.label || "").toLocaleLowerCase("fr-FR");
  const subject = article.endsWith("'") ? `${article}${name}` : `${article} ${name}`;

  return (
    <span className="gem-bourse-tip">
      <span className="gem-bourse-tip__lead">
        {`Le taux du jour ${subject} est calculé selon les indicateurs boursiers suivants :`}
      </span>
      {(basket || []).map((part) => (
        <span className="gem-bourse-tip__line" key={`${part.label}-${part.weight}`}>
          {`${Math.round(Number(part.weight) * 100)} % ${part.label}`}
        </span>
      ))}
      <span className="gem-bourse-tip__note">Ce taux est arrêté la veille à 23 h.</span>
    </span>
  );
}

function courseCaption(course) {
  if (!Number.isInteger(course)) return "Cours indisponible";
  if (course > 1) return `${course} pierres`;
  return `${course} pierre`;
}

function ownedCaption(gem) {
  const owned = Number(gem.count) || 0;
  if (owned < 1) {
    return FEMININE_GEM_IDS.has(gem.id)
      ? "Vous n'en possédez aucune."
      : "Vous n'en possédez aucun.";
  }
  return `Vous en possédez ${owned}.`;
}

function pluralGemName(label, count) {
  const name = String(label || "").toLocaleLowerCase("fr-FR");
  if (Number(count) > 1 && !name.endsWith("s")) return `${name}s`;
  return name;
}

function commonStonesPhrase(total) {
  const count = Number(total) || 0;
  return count > 1 ? `${count} pierres communes` : `${count} pierre commune`;
}

function allocationLines(allocations) {
  const order = new Map(FILLER_GEM_ORDER.map((id, index) => [id, index]));
  return Object.entries(allocations || {})
    .filter(([, count]) => Number(count) > 0)
    .sort(
      ([leftId], [rightId]) =>
        (order.get(leftId) ?? 99) - (order.get(rightId) ?? 99)
    )
    .map(([id, count]) => {
      const gem = GEM_BY_ID.get(id);
      return {
        id,
        label: gem?.label || id,
        imageUrl: gem?.imageUrl || "",
        count: Number(count),
      };
    });
}

function GemBourseResultModal({ result, onClose }) {
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const sentence = `Vous avez échangé ${result.quantity} ${pluralGemName(
    result.label,
    result.quantity
  )} contre ${commonStonesPhrase(result.commonTotal)} réparti comme suit :`;

  return createPortal(
    <div className="gem-bourse-result-backdrop" role="presentation">
      <div
        className="gem-bourse-result"
        role="dialog"
        aria-modal="true"
        aria-labelledby="gem-bourse-result-lead"
      >
        <p id="gem-bourse-result-lead" className="gem-bourse-result__lead">
          {sentence}
        </p>
        <ul className="gem-bourse-result__grid">
          {result.lines.map((line) => (
            <li key={line.id} className="gem-bourse-result__item">
              <MenuIconTooltip
                tooltip={line.label}
                placement="above"
                portal
                zIndex={2147483002}
              >
                <span className="gem-bourse-result__thumb" tabIndex={0}>
                  <img src={line.imageUrl} alt="" />
                </span>
              </MenuIconTooltip>
              <span className="gem-bourse-result__count">{line.count}</span>
            </li>
          ))}
        </ul>
        <button
          type="button"
          className="gem-bourse-result__ok"
          autoFocus
          onClick={onClose}
        >
          OK
        </button>
      </div>
    </div>,
    getAppPortalRoot()
  );
}

function rangeRatio(course, floor, ceiling) {
  const span = ceiling - floor;
  if (!Number.isInteger(course) || span <= 0) return null;
  return Math.min(1, Math.max(0, (course - floor) / span));
}

function markStyle(ratio) {
  if (ratio == null) return null;
  return { left: `calc(6px + (100% - 12px) * ${ratio})` };
}

const CONVERT_CYCLE_MS = 7100;
const CONVERT_VALIDATE_AT_MS = 3550;
const CONVERT_VALIDATE_UNTIL_MS = 6550;

function settleConvertOnValidate(button) {
  const amount = button.querySelector(".gem-bourse-convert__line--amount");
  const validate = button.querySelector(".gem-bourse-convert__line--validate");
  if (!amount || !validate || button.classList.contains("gem-bourse-convert--hold")) return;
  const amountFrom = getComputedStyle(amount).transform;
  const validateFrom = getComputedStyle(validate).transform;
  const timing = {
    duration: 500,
    easing: "cubic-bezier(0.4, 0, 0.2, 1)",
    fill: "forwards",
  };
  amount.animate(
    [{ transform: amountFrom }, { transform: "translateY(120%)" }],
    timing
  );
  validate.animate(
    [{ transform: validateFrom }, { transform: "translateY(0)" }],
    timing
  );
}

function releaseConvertCycle(button) {
  if (button.matches(":hover") || button.matches(":focus")) return;
  button.querySelectorAll(".gem-bourse-convert__line").forEach((line) => {
    line.getAnimations().forEach((animation) => {
      if (!animation.animationName) animation.cancel();
    });
  });
  window.requestAnimationFrame(() => {
    if (button.matches(":hover") || button.matches(":focus")) return;
    button.querySelectorAll(".gem-bourse-convert__line").forEach((line) => {
      line.getAnimations().forEach((animation) => {
        if (animation.animationName) animation.currentTime = CONVERT_VALIDATE_AT_MS;
      });
    });
  });
}

function SkelText({ children, className = "" }) {
  return (
    <span className={`gem-skel${className ? ` ${className}` : ""}`}>
      <span className="gem-bourse-skel__ghost">{children}</span>
    </span>
  );
}

function GemBourseSkeleton({ gems }) {
  const rows = gems.length
    ? gems
    : [
        { id: "spinelle", label: "Spinelle", count: 10 },
        { id: "péridot", label: "Péridot", count: 10 },
        { id: "tanzanite", label: "Tanzanite", count: 11 },
        { id: "émeraude", label: "Émeraude", count: 21 },
        { id: "saphir", label: "Saphir", count: 6 },
        { id: "rubis", label: "Rubis", count: 10 },
        { id: "diamant", label: "Diamant", count: 5 },
      ];

  return (
    <div className="gem-bourse gem-bourse--skeleton" aria-busy="true">
      <p className="gem-bourse-skel__status">Chargement de la bourse</p>
      <div className="gem-bourse__indicator">
        <SkelText>indicateur secondaire :</SkelText>
        <span className="gem-skel gem-bourse-skel__menu">
          <span className="gem-bourse-skel__ghost">J-1</span>
        </span>
      </div>
      <table className="gem-bourse-table">
        <thead>
          <tr>
            <th className="gem-bourse-table__blank" aria-hidden="true" />
            <th>
              <SkelText>Qtté à convertir</SkelText>
            </th>
            <th>
              <span className="gem-skel gem-bourse-skel__rate-head">
                <span className="gem-bourse-sort">
                  <span className="gem-bourse-skel__ghost">Taux du jour</span>
                  <span className="gem-bourse-skel__sort-slot" />
                </span>
              </span>
            </th>
            <th>
              <SkelText>Plage de valeur</SkelText>
            </th>
            <th>
              <SkelText>Transaction</SkelText>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((gem) => (
            <tr key={gem.id}>
              <td>
                <div className="gem-bourse-stone">
                  <span className="gem-bourse-stone__image gem-skel gem-bourse-skel__stone" />
                  <div className="gem-bourse-stone__text">
                    <p className="gem-bourse-stone__name">
                      <SkelText>{gem.label}</SkelText>
                    </p>
                    <p className="gem-bourse-stone__owned">
                      <SkelText>{ownedCaption(gem)}</SkelText>
                    </p>
                  </div>
                </div>
              </td>
              <td>
                <label className="gem-bourse-qty">
                  <input
                    className="gem-bourse-qty__input gem-skel"
                    disabled
                    readOnly
                    tabIndex={-1}
                    aria-hidden="true"
                    value=""
                  />
                </label>
              </td>
              <td className="gem-bourse-table__rate">
                <span className="gem-bourse-rate">
                  <span className="gem-skel gem-bourse-skel__info" />
                  <span className="gem-bourse-rate__value">
                    <SkelText>00 pierres</SkelText>
                  </span>
                </span>
              </td>
              <td>
                <div className="gem-bourse-scale">
                  <span className="gem-bourse-scale__bar gem-skel" />
                </div>
              </td>
              <td>
                <div className="gem-bourse-transaction">
                  <span className="gem-bourse-convert gem-skel">
                    <span className="gem-bourse-skel__ghost">Convertir</span>
                  </span>
                  <span className="gem-bourse-clear gem-skel gem-bourse-skel__clear" />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function GemBoursePanel({ gems, onExchanged }) {
  const [rates, setRates] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [comparisonLag, setComparisonLag] = useState("1");
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState("");
  const [busyId, setBusyId] = useState("");
  const [rateSortDesc, setRateSortDesc] = useState(true);
  const [result, setResult] = useState(null);
  const [heldConvertId, setHeldConvertId] = useState("");
  const [validatePhase, setValidatePhase] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const response = await fetch(`${API_URL}/gems/bourse`);
        const json = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(json.message || "Cours indisponible.");
        }
        if (!cancelled) setRates(Array.isArray(json.gems) ? json.gems : []);
      } catch (error) {
        if (!cancelled) {
          setRates([]);
          setNotice(error.message || "Cours indisponible.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const filledId = Object.keys(quantities).find((gemId) => quantities[gemId]) || "";

  const handleQuantity = useCallback((gemId, raw, owned) => {
    const cap = Math.min(QUANTITY_MAX, Math.max(0, Math.floor(Number(owned) || 0)));
    const digits = String(raw || "").replace(/\D/g, "").slice(0, 4);
    if (!digits || Number(digits) < 1 || cap < 1) {
      setQuantities((current) => ({ ...current, [gemId]: "" }));
      return;
    }
    const next = Math.min(cap, Number(digits));
    setQuantities((current) => ({ ...current, [gemId]: String(next) }));
  }, []);

  const clearQuantity = useCallback((gemId) => {
    setQuantities((current) => ({ ...current, [gemId]: "" }));
  }, []);

  const handleExchange = useCallback(
    async (gem) => {
      const quantity = Math.floor(Number(quantities[gem.id]));
      const owned = Number(gem.count) || 0;
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > owned) {
        setNotice(`Stock insuffisant pour ${gem.label}.`);
        return;
      }

      setBusyId(gem.id);
      setNotice("");
      try {
        const response = await fetch(`${API_URL}/gems/bourse/exchange`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ preciousGemId: gem.id, quantity }),
        });
        const json = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(json.message || "Conversion impossible.");
        }
        setNotice("");
        setResult({
          label: gem.label,
          quantity,
          commonTotal: Number(json.commonTotal) || 0,
          lines: allocationLines(json.allocations),
        });
        setQuantities((current) => ({ ...current, [gem.id]: "" }));
        if (json.gemCounts) onExchanged?.({ gemCounts: json.gemCounts });
      } catch (error) {
        setNotice(error.message || "Conversion impossible.");
      } finally {
        setBusyId("");
      }
    },
    [onExchanged, quantities]
  );

  const ratesById = new Map(rates.map((rate) => [rate.id, rate]));

  useEffect(() => {
    if (!filledId) {
      setValidatePhase(false);
      return undefined;
    }
    if (heldConvertId) {
      setValidatePhase(true);
      return undefined;
    }

    const readPhase = () => {
      const line = document.querySelector(".gem-bourse-convert__line--validate");
      const animation = [...(line?.getAnimations() || [])].find((item) => item.animationName);
      if (!animation || animation.currentTime == null) return false;
      const time = Number(animation.currentTime) % CONVERT_CYCLE_MS;
      return time >= CONVERT_VALIDATE_AT_MS && time < CONVERT_VALIDATE_UNTIL_MS;
    };

    const tick = () => {
      const next = readPhase();
      setValidatePhase((current) => (current === next ? current : next));
    };

    tick();
    const timer = window.setInterval(tick, 80);
    return () => window.clearInterval(timer);
  }, [filledId, heldConvertId]);

  if (loading) {
    return <GemBourseSkeleton gems={gems} />;
  }

  const orderedGems = [...gems].sort((left, right) => {
    const leftCourse = ratesById.get(left.id)?.course;
    const rightCourse = ratesById.get(right.id)?.course;
    const leftKnown = Number.isInteger(leftCourse);
    const rightKnown = Number.isInteger(rightCourse);
    if (leftKnown && rightKnown && leftCourse !== rightCourse) {
      return rateSortDesc ? rightCourse - leftCourse : leftCourse - rightCourse;
    }
    if (leftKnown !== rightKnown) return leftKnown ? -1 : 1;
    return 0;
  });

  return (
    <div className="gem-bourse" aria-busy={loading || undefined}>
      <div className="gem-bourse__indicator">
        <span>indicateur secondaire :</span>
        <div className="gem-bourse__indicator-dropdown">
          <PackAssignDropdown
            options={COMPARISON_OPTIONS}
            value={comparisonLag}
            onChange={setComparisonLag}
            ariaLabel="Indicateur secondaire"
            matchTriggerWidth
            menuMaxHeight={180}
            menuClassName="gem-bourse__indicator-menu"
          />
        </div>
      </div>
      {notice ? <p className="gem-bourse__notice">{notice}</p> : null}
      <table className="gem-bourse-table">
        <thead>
          <tr>
            <th className="gem-bourse-table__blank" aria-label="Pierre" />
            <th>Qtté à convertir</th>
            <th>
              <span className="gem-bourse-sort">
                Taux du jour
                <button
                  type="button"
                  className={`gem-bourse-sort__btn${
                    rateSortDesc ? " gem-bourse-sort__btn--up" : " gem-bourse-sort__btn--down"
                  }`}
                  aria-label={
                    rateSortDesc
                      ? "Afficher les taux les plus bas en premier"
                      : "Afficher les taux les plus élevés en premier"
                  }
                  onClick={() => setRateSortDesc((current) => !current)}
                />
              </span>
            </th>
            <th>Plage de valeur</th>
            <th className={filledId ? "gem-bourse-table__receive" : undefined}>
              {!filledId ? "Transaction" : validatePhase ? "Transaction..." : "Vous recevrez..."}
            </th>
          </tr>
        </thead>
        <tbody>
          {orderedGems.map((gem) => {
            const rate = ratesById.get(gem.id);
            const course = Number.isInteger(rate?.course) ? rate.course : null;
            const floor = Number.isInteger(rate?.floor) ? rate.floor : null;
            const ceiling = Number.isInteger(rate?.ceiling) ? rate.ceiling : null;
            const pastCourse = Number.isInteger(rate?.comparisons?.[comparisonLag])
              ? rate.comparisons[comparisonLag]
              : null;
            const ratio =
              floor != null && ceiling != null ? rangeRatio(course, floor, ceiling) : null;
            const pastRatio =
              floor != null && ceiling != null ? rangeRatio(pastCourse, floor, ceiling) : null;
            const owned = Number(gem.count) || 0;
            const quantityText = quantities[gem.id] || "";
            const quantity = Math.floor(Number(quantityText));
            const locked = Boolean(filledId) && filledId !== gem.id;
            const inputDisabled = locked || owned < 1 || busyId === gem.id;
            const payout =
              quantity >= 1 && course != null ? quantity * course : null;
            const payoutLabel =
              payout == null
                ? "Convertir"
                : payout > 1
                  ? `${payout} pierres`
                  : `${payout} pierre`;
            const canConvert =
              !loading &&
              !locked &&
              course != null &&
              quantity >= 1 &&
              quantity <= owned &&
              busyId !== gem.id;

            return (
              <tr key={gem.id}>
                <td>
                  <div className="gem-bourse-stone">
                    <img
                      src={gem.imageUrl}
                      alt=""
                      className="gem-bourse-stone__image"
                      decoding="sync"
                    />
                    <div className="gem-bourse-stone__text">
                      <p className="gem-bourse-stone__name">{gem.label}</p>
                      <p className="gem-bourse-stone__owned">{ownedCaption(gem)}</p>
                    </div>
                  </div>
                </td>
                <td>
                  <label className="gem-bourse-qty">
                    <input
                      className="gem-bourse-qty__input"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      maxLength={4}
                      disabled={inputDisabled}
                      value={quantityText}
                      aria-label={`Nombre de ${gem.label} à convertir`}
                      onChange={(event) => handleQuantity(gem.id, event.target.value, owned)}
                    />
                  </label>
                </td>
                <td className="gem-bourse-table__rate">
                  <span className="gem-bourse-rate">
                    {rate?.basket?.length ? (
                      <VocabInfoTip
                        className="gem-bourse-rate__info"
                        tooltip={basketTooltip(gem, rate.basket)}
                        ariaLabel={`Composition du cours de ${gem.label}`}
                      />
                    ) : null}
                    <span className="gem-bourse-rate__value">
                      {loading ? "…" : courseCaption(course)}
                    </span>
                  </span>
                </td>
                <td>
                  <div
                    className="gem-bourse-scale"
                    aria-label={
                      course != null && floor != null && ceiling != null
                        ? `Cours ${course}, entre ${floor} et ${ceiling}`
                        : "Échelle de cours indisponible"
                    }
                  >
                    <span className="gem-bourse-scale__bar" />
                    {ratio != null ? (
                      <span className="gem-bourse-scale__mark" style={markStyle(ratio)} />
                    ) : null}
                    {pastRatio != null ? (
                      <svg
                        className="gem-bourse-scale__past"
                        style={markStyle(pastRatio)}
                        viewBox="0 0 12 8"
                        aria-hidden="true"
                      >
                        <polygon
                          points="6,0.8 11.2,7.2 0.8,7.2"
                          fill="none"
                          stroke="#1a120c"
                          strokeWidth="1.1"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : null}
                  </div>
                </td>
                <td>
                  <div className="gem-bourse-transaction">
                    <button
                      type="button"
                      className={`gem-bourse-convert${
                        payout != null ? " gem-bourse-convert--payout" : ""
                      }${
                        heldConvertId === gem.id ? " gem-bourse-convert--hold" : ""
                      }`}
                      disabled={!canConvert}
                      aria-label={payout != null ? `${payoutLabel}. Valider` : undefined}
                      onMouseEnter={(event) => {
                        if (payout == null) return;
                        settleConvertOnValidate(event.currentTarget);
                        setHeldConvertId(gem.id);
                      }}
                      onMouseLeave={(event) => {
                        const button = event.currentTarget;
                        setHeldConvertId((current) => (current === gem.id ? "" : current));
                        releaseConvertCycle(button);
                      }}
                      onFocus={(event) => {
                        if (payout == null) return;
                        settleConvertOnValidate(event.currentTarget);
                        setHeldConvertId(gem.id);
                      }}
                      onBlur={(event) => {
                        const button = event.currentTarget;
                        setHeldConvertId((current) => (current === gem.id ? "" : current));
                        releaseConvertCycle(button);
                      }}
                      onClick={() => handleExchange(gem)}
                    >
                      {payout != null ? (
                        <>
                          <span className="gem-bourse-convert__sizer">{payoutLabel}</span>
                          <span className="gem-bourse-convert__viewport" aria-hidden="true">
                            <span className="gem-bourse-convert__line gem-bourse-convert__line--amount">
                              {payoutLabel}
                            </span>
                            <span className="gem-bourse-convert__line gem-bourse-convert__line--validate">
                              Valider
                            </span>
                          </span>
                        </>
                      ) : (
                        payoutLabel
                      )}
                    </button>
                    <button
                      type="button"
                      className="gem-bourse-clear"
                      aria-label={`Vider la quantité de ${gem.label}`}
                      disabled={!quantityText}
                      onClick={() => clearQuantity(gem.id)}
                    >
                      <span aria-hidden="true">×</span>
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {result ? (
        <GemBourseResultModal result={result} onClose={() => setResult(null)} />
      ) : null}
    </div>
  );
}
